import { Minus, Plus, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type WheelEvent } from 'react'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from './Button'
import { Dialog } from './Dialog'

/**
 * How a photo is cut. `circle` — a person's avatar, cut square (1:1) and shown
 * through a round mask, as Telegram does; `rect` — a picture of a thing
 * (vehicle, product, category…) cut to `aspect` and shown as a rectangle.
 */
export type ImageCrop = {
  shape: 'circle' | 'rect'
  /** Width / height of the cut; a circle is always 1. */
  aspect?: number
  /** Width of the saved file in px; a smaller source is never scaled up. */
  outputWidth?: number
}

export const AVATAR_CROP: ImageCrop = { shape: 'circle', outputWidth: 512 }
/** Every other picture (vehicles, products, categories, slips…): a square frame, never a circle. */
export const SQUARE_CROP: ImageCrop = { shape: 'rect', aspect: 1, outputWidth: 1024 }

const MAX_ZOOM = 4
/** The frame stands this far inside the viewport, so the dimmed rest of the photo shows around it. */
const INSET = 20
const MAX_VIEW_WIDTH = 360

type Offset = { x: number; y: number }

const aspectOf = (crop: ImageCrop) => (crop.shape === 'circle' ? 1 : (crop.aspect ?? 1))

/**
 * Cuts a photo before it is uploaded, or re-cuts one already uploaded: zoom
 * (slider, wheel, + / −) and drag (pointer or arrow keys) the photo under a
 * fixed frame — round for an avatar, rectangular otherwise. An avatar always
 * fills its circle; a rectangle can also zoom out until the whole picture fits
 * (a long car), the empty edges staying transparent. Resolves with the cut
 * file, or nothing when cancelled.
 */
export function ImageCropDialog({ file, crop, onDone }: { file: File; crop: ImageCrop; onDone: (cropped: File | null) => void }) {
  const t = useT()
  const aspect = aspectOf(crop)
  const round = crop.shape === 'circle'
  // Fixed for the dialog's life: the maths below works in these pixels. Narrower on a phone.
  const [viewWidth] = useState(() => Math.min(MAX_VIEW_WIDTH, window.innerWidth - 80))
  const frame = { width: viewWidth - INSET * 2, height: (viewWidth - INSET * 2) / aspect }
  const view = { width: viewWidth, height: frame.height + INSET * 2 }

  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 })
  const [saving, setSaving] = useState(false)
  const drag = useRef<{ pointer: number; start: Offset; origin: Offset } | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    const element = new Image()
    element.onload = () => setImage(element)
    element.src = url
    return () => URL.revokeObjectURL(url)
  }, [file])

  // Zoom 1: the photo just covers the frame. A rectangle may go down to `minZoom`, where it just fits inside.
  const base = image ? Math.max(frame.width / image.naturalWidth, frame.height / image.naturalHeight) : 1
  const fit = image ? Math.min(frame.width / image.naturalWidth, frame.height / image.naturalHeight) : 1
  const minZoom = round ? 1 : fit / base
  const scale = base * zoom
  const sizeAt = (value: number) => (image ? { width: image.naturalWidth * base * value, height: image.naturalHeight * base * value } : { width: 0, height: 0 })
  const shown = sizeAt(zoom)

  /**
   * A photo larger than the frame keeps it covered (its edge never comes
   * inside); a smaller one (zoomed out) stays inside the frame.
   */
  const clamp = (next: Offset, size = shown): Offset => {
    const maxX = Math.abs(size.width - frame.width) / 2
    const maxY = Math.abs(size.height - frame.height) / 2
    return { x: Math.min(maxX, Math.max(-maxX, next.x)), y: Math.min(maxY, Math.max(-maxY, next.y)) }
  }

  /** Zooms about the frame's centre: the point in the middle stays in the middle. */
  const zoomTo = (value: number) => {
    if (!image) return
    const next = Math.min(MAX_ZOOM, Math.max(minZoom, value))
    const ratio = next / zoom
    setZoom(next)
    setOffset((current) => clamp({ x: current.x * ratio, y: current.y * ratio }, sizeAt(next)))
  }

  const reset = () => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!image || event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { pointer: event.pointerId, start: { x: event.clientX, y: event.clientY }, origin: offset }
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (!current || current.pointer !== event.pointerId) return
    setOffset(clamp({ x: current.origin.x + event.clientX - current.start.x, y: current.origin.y + event.clientY - current.start.y }))
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointer === event.pointerId) drag.current = null
  }
  const onWheel = (event: WheelEvent<HTMLDivElement>) => {
    zoomTo(zoom * (event.deltaY < 0 ? 1.08 : 1 / 1.08))
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 40 : 10
    const moves: Record<string, Offset> = { ArrowLeft: { x: step, y: 0 }, ArrowRight: { x: -step, y: 0 }, ArrowUp: { x: 0, y: step }, ArrowDown: { x: 0, y: -step } }
    if (moves[event.key]) {
      event.preventDefault()
      setOffset((current) => clamp({ x: current.x + moves[event.key].x, y: current.y + moves[event.key].y }))
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      zoomTo(zoom + 0.1)
    } else if (event.key === '-') {
      event.preventDefault()
      zoomTo(zoom - 0.1)
    }
  }

  // Where the photo sits in the viewport.
  const imageLeft = view.width / 2 + offset.x - shown.width / 2
  const imageTop = view.height / 2 + offset.y - shown.height / 2

  const save = async () => {
    if (!image) return
    setSaving(true)
    // The frame as it is on screen, scaled to the saved file: draw the photo where it sits.
    const sourceWidth = frame.width / scale
    const width = Math.max(1, Math.round(Math.min(crop.outputWidth ?? sourceWidth, sourceWidth)))
    const height = Math.max(1, Math.round(width / aspect))
    const k = width / frame.width
    const covers = shown.width >= frame.width - 0.5 && shown.height >= frame.height - 0.5
    // Transparent edges (zoomed out) and transparent PNGs stay PNG; a photo that fills the frame is a JPEG.
    const type = !round && (!covers || file.type === 'image/png') ? 'image/png' : 'image/jpeg'
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) {
      onDone(file)
      return
    }
    if (type === 'image/jpeg') {
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
    }
    context.imageSmoothingQuality = 'high'
    context.drawImage(image, (imageLeft - INSET) * k, (imageTop - INSET) * k, shown.width * k, shown.height * k)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.92))
    if (!blob) {
      onDone(file)
      return
    }
    const name = file.name.replace(/\.[^.]+$/, '') + (type === 'image/png' ? '.png' : '.jpg')
    onDone(new File([blob], name, { type }))
  }

  return (
    <Dialog
      open
      size="sm"
      onClose={() => !saving && onDone(null)}
      title={round ? t('workbench.image.crop.avatarTitle') : t('workbench.image.crop.title')}
      description={round ? t('workbench.image.crop.hint') : t('workbench.image.crop.rectHint')}
      footer={
        <>
          <Button variant="secondary" disabled={saving} onClick={() => onDone(null)}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="primary" loading={saving} disabled={!image} onClick={() => void save()}>
            {t('workbench.image.crop.apply')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-3">
        <div
          role="application"
          tabIndex={0}
          aria-label={t('workbench.image.crop.area')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          onKeyDown={onKeyDown}
          className={cn(
            'relative shrink-0 cursor-grab touch-none select-none overflow-hidden rounded-[calc(12px*var(--radius-scale))] outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-soft)] active:cursor-grabbing',
            // A checkerboard behind a rectangle shows which edges will be transparent.
            round ? 'bg-[#111]' : 'bg-[conic-gradient(#d9d9d9_25%,#f4f4f4_0_50%,#d9d9d9_0_75%,#f4f4f4_0)] bg-[length:16px_16px]',
          )}
          style={{ width: view.width, height: view.height }}
        >
          {image && (
            <img
              src={image.src}
              alt=""
              draggable={false}
              className="pointer-events-none absolute max-w-none"
              style={{ width: shown.width, height: shown.height, left: imageLeft, top: imageTop }}
            />
          )}
          {/* The frame: everything outside it is dimmed; a circle for an avatar, a rectangle otherwise. */}
          <div
            aria-hidden
            className={cn('pointer-events-none absolute border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]', round ? 'rounded-full' : 'rounded-[4px]')}
            style={{ left: INSET, top: INSET, width: frame.width, height: frame.height }}
          />
        </div>

        <div className="flex w-full items-center gap-2" style={{ maxWidth: view.width }}>
          <Button size="icon" variant="ghost" aria-label={t('workbench.image.crop.zoomOut')} disabled={!image || zoom <= minZoom} onClick={() => zoomTo(zoom - 0.25)}>
            <Minus className="size-4" />
          </Button>
          <input
            type="range"
            min={minZoom}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            disabled={!image}
            aria-label={t('workbench.image.crop.zoom')}
            onChange={(event) => zoomTo(Number(event.target.value))}
            className="h-1.5 min-w-0 flex-1 cursor-pointer accent-[var(--accent)]"
          />
          <Button size="icon" variant="ghost" aria-label={t('workbench.image.crop.zoomIn')} disabled={!image || zoom >= MAX_ZOOM} onClick={() => zoomTo(zoom + 0.25)}>
            <Plus className="size-4" />
          </Button>
          <Button size="icon" variant="ghost" aria-label={t('workbench.image.crop.reset')} title={t('workbench.image.crop.reset')} disabled={!image} onClick={reset}>
            <RotateCcw className="size-4" />
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
